import { useState } from "react";
import { Plus } from "lucide-react";
import { useLedgerAccounts, useSaveLedgerAccount, type LedgerAccount } from "@/api/books";
import { LedgerAccountForm } from "@/components/forms/LedgerAccountForm";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { formatInr } from "@/lib/format";

type Editing = { mode: "new" } | { mode: "edit"; account: LedgerAccount } | null;

const KIND_LABEL: Record<LedgerAccount["kind"], string> = {
  asset: "Asset",
  liability: "Liability",
  equity: "Equity",
  income: "Income",
  expense: "Expense",
};

export function ChartPanel() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const accounts = useLedgerAccounts(includeArchived);
  const save = useSaveLedgerAccount();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Checkbox
          id="chart-archived"
          label="Show archived accounts"
          checked={includeArchived}
          onChange={setIncludeArchived}
        />
        {editing === null && (
          <Button size="sm" onClick={() => setEditing({ mode: "new" })}>
            <Plus className="h-4 w-4" aria-hidden />
            Add account
          </Button>
        )}
      </div>
      {editing && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle>
              {editing.mode === "new" ? "New account" : `Edit ${editing.account.code} ${editing.account.name}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <LedgerAccountForm
              initial={editing.mode === "edit" ? editing.account : null}
              onSubmit={async (input) => {
                await save.mutateAsync({
                  input,
                  existing: editing.mode === "edit" ? editing.account : undefined,
                });
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
              onReload={() => {
                void accounts.refetch();
                setEditing(null);
              }}
            />
          </CardContent>
        </Card>
      )}
      <RequestError error={accounts.error} />
      <div className="table-frame">
        <table className="data-table">
          <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-medium">Code</th>
              <th className="px-4 py-2 font-medium">Account</th>
              <th className="px-4 py-2 font-medium">Kind</th>
              <th className="px-4 py-2 font-medium">Schedule III group</th>
              <th className="px-4 py-2 text-right font-medium">Balance</th>
              <th className="px-4 py-2">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {(accounts.data ?? []).map((account) => (
              <tr key={account.id} className="border-b last:border-0">
                <td className="px-4 py-2 text-muted-foreground">{account.code}</td>
                <td className="px-4 py-2">
                  <span className="flex flex-wrap items-center gap-2">
                    {account.name}
                    {account.system_key && <Badge variant="secondary">system</Badge>}
                    {account.swings && <Badge variant="muted">two-sided</Badge>}
                    {account.archived && <Badge variant="muted">archived</Badge>}
                  </span>
                </td>
                <td className="px-4 py-2">{KIND_LABEL[account.kind]}</td>
                <td className="px-4 py-2">{account.schedule_group}</td>
                <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                  {formatInr(account.balance_inr)}
                </td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEditing({ mode: "edit", account })}>
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Balances are debit minus credit over every entry: assets and expenses read positive, income
        and liabilities negative.
      </p>
    </div>
  );
}
